/**
 * Year in review - w2_18. Everything is worked out from userData.
 *
 * Net worth at the end of a month uses the same sums as the Wealth page and
 * Home's "this month" change (funds counted in net worth, investments with
 * their monthly overrides, cash, assets and debts).
 *
 * In someone's first year with Worthly, nothing is compared to a year before:
 * everything says "since {the month you started}".
 */
import { getNetWorthFunds } from "./netWorthMath";
import { parseCategoryEmoji } from "./categoryEmoji";
import { investmentBalance, monthEndDay } from "./investMath"; // dark_13: one sum for investment balances

const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
const sum = (list, f = (t) => n(t.amount)) => list.reduce((s, t) => s + f(t), 0);
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const mkey = (y, m) => `${y}-${String(m + 1).padStart(2, '0')}`; // m is 0-11
const RENT = /\b(rent|mortgage|housing|condo fees?|maintenance fees?|strata)\b/i;

const txDate = (t) => { const d = t && t.date ? new Date(t.date) : null; return d && !isNaN(d) ? d : null; };

/** Net worth at the end of a month (y, m 0-11). */
export const netWorthAtMonthEnd = (u, y, m) => {
  const key = mkey(y, m);
  const cutoff = new Date(y, m + 1, 0, 23, 59, 59);
  const tx = u.transactions || [];
  const byDate = tx.filter((t) => { const d = txDate(t); return d && d <= cutoff; });
  const byKey = tx.filter((t) => t.month_key === key);
  const funds = getNetWorthFunds(u.sinking_funds || []);
  const acct = (id, type) => {
    const dep = sum(byDate.filter((t) => t.account_id === id && t.account_type === type && t.transaction_type === 'deposit'));
    const wd = sum(byDate.filter((t) => t.account_id === id && t.account_type === type && t.transaction_type === 'withdrawal'));
    const int = sum(byDate.filter((t) => t.interest_account_id === id && t.interest_account_type === type && t.is_interest));
    return dep + int - wd;
  };
  const fundsTotal = funds.reduce((s, f) => s + n(f.starting_balance) + acct(f.id, 'fund'), 0);
  // dark_13: the same investment sum as everywhere else
  const invTotal = (u.investment_accounts || []).reduce((s, a) => s + investmentBalance(a, tx, key, { cutoffDay: monthEndDay(key) }), 0);
  const assets = (u.assets || []).reduce((s, a) => {
    const cbm = a.current_by_month || {};
    if (cbm[key] !== undefined) return s + n(cbm[key]);
    const keys = Object.keys(cbm).filter((k) => k <= key).sort();
    return s + (keys.length ? n(cbm[keys[keys.length - 1]]) : 0);
  }, 0);
  const debts = (u.debts || []).reduce((s, d) => {
    let start;
    if (d.starting_balance !== undefined) start = n(d.starting_balance);
    else {
      const keys = Object.keys(d.current_by_month || {}).filter((k) => k <= key).sort();
      start = keys.length ? n(d.current_by_month[keys[keys.length - 1]]) : n(d.original_amount);
    }
    const paid = sum(byDate.filter((t) => t.is_debt_payment && t.debt_id === d.id));
    return s + Math.max(0, start - paid);
  }, 0);
  const bs = u.budget_settings || {};
  const expenses = sum(byKey.filter((t) => !t.is_income && !t.is_savings));
  const income = sum(byKey.filter((t) => t.is_income && !t.is_from_savings && !t.is_interest));
  const sDep = sum(byKey.filter((t) => t.is_savings && t.transaction_type !== 'withdrawal'));
  const sWd = sum(byKey.filter((t) => t.is_savings && t.transaction_type === 'withdrawal'));
  const leftover = n((bs.leftover_by_month || {})[key]);
  const cash = (leftover + income + sWd) - expenses - sDep;
  return fundsTotal + invTotal + cash + assets - debts;
};

/** The first date anything was logged. */
export const firstActivity = (u) => {
  let first = null;
  (u.transactions || []).forEach((t) => { const d = txDate(t); if (d && (!first || d < first)) first = d; });
  return first;
};

/** Does this year have enough for a review? At least one finished month with something logged. */
export const hasYearReview = (u, year, today = new Date()) => {
  const first = firstActivity(u);
  if (!first || first.getFullYear() > year) return false;
  const lastMonth = year < today.getFullYear() ? 11 : today.getMonth();
  const startMonth = first.getFullYear() < year ? 0 : first.getMonth();
  return lastMonth >= startMonth && (u.transactions || []).some((t) => String(t.month_key || '').startsWith(year + '-'));
};

/** The year whose review is showing now: December shows this year, January shows last year. */
export const reviewYearFor = (today = new Date()) => {
  if (today.getMonth() === 11 && today.getDate() >= 15) return today.getFullYear();
  if (today.getMonth() === 0) return today.getFullYear() - 1;
  return null;
};

export const buildYearReview = (u, year, today = new Date()) => {
  const first = firstActivity(u);
  const firstYear = !first || first.getFullYear() >= year;
  const startMonth = first && first.getFullYear() === year ? first.getMonth() : 0;
  const endMonth = year < today.getFullYear() ? 11 : today.getMonth();
  const inYear = (t) => String(t.month_key || '').startsWith(year + '-');
  const tx = (u.transactions || []).filter(inYear);
  const expenses = tx.filter((t) => !t.is_income && !t.is_savings && !t.is_transfer);

  // --- net worth, month by month
  const series = [];
  // In a first year the line starts at the end of the first month (that's when you set things up).
  const startVal = firstYear ? netWorthAtMonthEnd(u, year, startMonth) : netWorthAtMonthEnd(u, year - 1, 11);
  const fromMonth = firstYear ? startMonth : 0;
  if (!firstYear) series.push({ m: -1, v: startVal });
  for (let m = fromMonth; m <= endMonth; m += 1) series.push({ m, v: netWorthAtMonthEnd(u, year, m) });
  const endVal = series.length ? series[series.length - 1].v : startVal;
  let best = null;
  for (let i = 1; i < series.length; i += 1) {
    const jump = series[i].v - series[i - 1].v;
    if (series[i].m >= 0 && (!best || jump > best.jump)) best = { m: series[i].m, jump };
  }
  // "Biggest year yet" only with a full year before to compare to.
  let biggestYet = false;
  if (!firstYear && first && first <= new Date(year - 1, 0, 31, 23, 59, 59)) {
    const prevGrowth = netWorthAtMonthEnd(u, year - 1, 11) - netWorthAtMonthEnd(u, year - 2, 11);
    biggestYet = endVal - startVal > prevGrowth && endVal - startVal > 0;
  }

  // --- where it went (rent left out)
  const cats = {};
  expenses.forEach((t) => {
    const c = t.category || 'Other';
    if (RENT.test(String(c))) return;
    cats[c] = (cats[c] || 0) + n(t.amount);
  });
  const top = Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([c, total]) => {
    const p = parseCategoryEmoji(c);
    return { category: c, emoji: (p && p.emoji) || '\u{1F4B8}', name: (p && p.name) || c, total };
  });

  // --- saved and invested (deposits minus withdrawals, like the monthly review)
  const savingsTx = tx.filter((t) => t.is_savings);
  const saved = sum(savingsTx.filter((t) => t.transaction_type !== 'withdrawal')) - sum(savingsTx.filter((t) => t.transaction_type === 'withdrawal')) - sum(tx.filter((t) => t.is_from_savings));

  // --- sinking funds
  const fundTx = (u.transactions || []).filter((t) => inYear(t) && t.account_type === 'fund');
  const funds = (u.sinking_funds || []).map((f) => {
    const added = sum(fundTx.filter((t) => t.account_id === f.id && t.transaction_type === 'deposit'));
    const used = sum(fundTx.filter((t) => t.account_id === f.id && t.transaction_type === 'withdrawal'));
    const all = (u.transactions || []).filter((t) => { const d = txDate(t); return d && d <= new Date(year, endMonth + 1, 0, 23, 59, 59); });
    const bal = n(f.starting_balance)
      + sum(all.filter((t) => t.account_id === f.id && t.account_type === 'fund' && t.transaction_type === 'deposit'))
      + sum(all.filter((t) => t.interest_account_id === f.id && t.interest_account_type === 'fund' && t.is_interest))
      - sum(all.filter((t) => t.account_id === f.id && t.account_type === 'fund' && t.transaction_type === 'withdrawal'));
    const goal = n(f.goal);
    return { id: f.id, name: f.name, emoji: f.emoji || '\u{1F437}', added, used, balance: bal, goal, full: goal > 0 && bal >= goal - 0.004 };
  });
  const fundsAdded = sum(funds, (f) => f.added);
  const fundsUsed = sum(funds, (f) => f.used);
  const topFunds = funds.filter((f) => f.added > 0).sort((a, b) => b.added - a.added).slice(0, 5);
  const maxBal = Math.max(1, ...topFunds.map((f) => f.balance));
  topFunds.forEach((f) => { f.fill = f.goal > 0 ? Math.min(1, Math.max(0, f.balance / f.goal)) : Math.min(1, Math.max(0, f.balance / maxBal)); });
  const filled = funds.filter((f) => f.full && f.added > 0);
  const growMost = topFunds[0] || null;

  // --- no-spend days and willpower wins
  const ns = u.no_spend_days || {};
  let noSpend = 0; let bestNs = null;
  for (let m = 0; m < 12; m += 1) {
    const days = Array.from(new Set(((ns[mkey(year, m)] || [])).map(Number))).length;
    noSpend += days;
    if (days > 0 && (!bestNs || days > bestNs.days)) bestNs = { m, days };
  }
  const wins = (u.saved_items || []).filter((i) => String(i.date || '').startsWith(year + '-'));
  const winsAmount = sum(wins, (i) => n(i.amount || i.value));

  return {
    year,
    name: (u.display_name || '').trim() && u.display_name !== 'User' ? u.display_name.trim() : '',
    firstYear,
    startMonth,
    endMonth,
    purchases: expenses.length,
    netWorth: { start: startVal, end: endVal, change: endVal - startVal, series, best, biggestYet, fromMonth: firstYear ? startMonth : null },
    top,
    saved,
    funds: { added: fundsAdded, used: fundsUsed, top: topFunds, filled, growMost },
    noSpend: { days: noSpend, best: bestNs, wins: wins.length, winsAmount },
  };
};
